import type { Student } from "@/lib/student.schema";
import { YEAR_LABELS, canonicalSubject } from "./score-labels";

interface HighSchoolScoresProps {
	student: Student;
	/** Bỏ trống để ẩn tiêu đề. */
	title?: string | null;
}

const CONDUCT_LABELS: Record<string, string> = {
	T: "Tốt",
	K: "Khá",
	TB: "Trung bình",
	Y: "Yếu",
	KEM: "Kém",
};

/** Parse "Toán: 8.5, Lý: 7.0" (comma or semicolon separated) into subject -> score. */
function parseYearScores(raw: string | null): Map<string, string> {
	const scores = new Map<string, string>();
	if (!raw) return scores;

	for (const part of raw.split(/[,;]/)) {
		const [name, ...rest] = part.split(":");
		const subject = name?.trim();
		const score = rest.join(":").trim();
		// Đưa tên môn về tên chuẩn ngay tại chỗ đọc dữ liệu: nguồn viết "Vật lí" ở
		// cột này và "Vật lý" ở cột `VatLy_HB`, nên không chuẩn hoá thì hai bảng
		// xếp trên dưới nhau gọi cùng một môn bằng hai tên. Chuẩn hoá ở đây cũng
		// làm khoá của Map đồng nhất, nên môn cùng nghĩa không tách thành hai hàng.
		if (subject && score) scores.set(canonicalSubject(subject), score);
	}
	return scores;
}

/** Parse "Lớp 10: T; Lớp 11: K; Lớp 12: TB" into one readable label per year. */
function parseConduct(raw: string | null): string[] {
	const codes = raw ? raw.split(";").map((item) => item.split(":")[1]?.trim() ?? "") : [];

	return YEAR_LABELS.map((_, index) => {
		const code = codes[index];
		if (!code) return "-";
		return CONDUCT_LABELS[code.toUpperCase()] ?? code;
	});
}

/**
 * Điểm các môn qua 3 năm THPT — hiển thị dạng ma trận môn × lớp,
 * kèm hàng kết quả học tập từng năm.
 */
export default function HighSchoolScores({
	student,
	title = "Điểm 3 năm THPT",
}: HighSchoolScoresProps) {
	const yearScores = [student.diem_lop_10, student.diem_lop_11, student.diem_lop_12].map(
		parseYearScores,
	);
	const conduct = parseConduct(student.kq_hoctap);

	// Subject order follows first appearance across lớp 10 → 12
	const subjects: string[] = [];
	for (const scores of yearScores) {
		for (const subject of scores.keys()) {
			if (!subjects.includes(subject)) subjects.push(subject);
		}
	}

	return (
		<div className="rounded-lg border-2 border-yellow-300 bg-yellow-50 p-3 space-y-2">
			{title ? <div className="text-base font-bold text-yellow-900">{title}</div> : null}

			{subjects.length === 0 ? (
				<div className="text-sm font-medium text-yellow-700">Chưa có điểm</div>
			) : (
				<table className="w-full table-fixed">
					<thead>
						<tr className="border-b border-yellow-300">
							<th className="py-1 text-left text-sm font-semibold text-yellow-800">Môn</th>
							{YEAR_LABELS.map((label) => (
								<th key={label} className="py-1 text-center text-sm font-semibold text-yellow-800">
									{label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{subjects.map((subject) => (
							<tr key={subject} className="border-b border-yellow-200 last:border-b-0">
								<td className="py-1 text-left text-sm font-semibold text-yellow-900">{subject}</td>
								{yearScores.map((scores, index) => (
									<td
										key={YEAR_LABELS[index]}
										className="py-1 text-center text-lg font-bold text-yellow-700"
									>
										{scores.get(subject) || "-"}
									</td>
								))}
							</tr>
						))}
					</tbody>
					<tfoot>
						<tr className="border-t-2 border-yellow-300">
							<td className="py-1 text-left text-sm font-semibold text-yellow-800">KQ học tập</td>
							{conduct.map((label, index) => (
								<td
									key={YEAR_LABELS[index]}
									className="py-1 text-center text-sm font-semibold text-yellow-700"
								>
									{label}
								</td>
							))}
						</tr>
					</tfoot>
				</table>
			)}
		</div>
	);
}
